import { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { FaTimes } from 'react-icons/fa';
import { MdKeyboardArrowDown } from 'react-icons/md';

import { Button } from 'components/common/button';

import { useOnClickOutside } from 'hooks';

import './Picker.css';

const Picker = ({ data, type, value, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pickedValue, setPickedValue] = useState(value);
    const pickerRef = useRef(null);

    let ariaLabel = '';

    if (type === 'country') {
        ariaLabel = 'Choose your language';
    } else if (type === 'currency') {
        ariaLabel = 'Choose your currency';
    }

    const handleTiggerBtnClick = useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const renderItems = () => {
        const MIN_NUMBER_OF_ITEMS_IN_COL = 10;
        const MAX_NUMBER_OF_COLS = 3;

        const numberOfItems = data?.length || 0;
        const numberOfItemsInCol = Math.ceil(
            numberOfItems / MAX_NUMBER_OF_COLS
        );
        const actualNumberOfItemsInCol =
            numberOfItemsInCol > MIN_NUMBER_OF_ITEMS_IN_COL
                ? numberOfItemsInCol
                : MIN_NUMBER_OF_ITEMS_IN_COL;

        const cols = [];
        let col = [];
        for (let i = 0; i < data?.length; i++) {
            const item = data[i];
            let itemJSX;
            let isSelected;

            if (type === 'country') {
                isSelected = pickedValue?.countryName === item?.countryName;
                itemJSX = (
                    <div
                        className={`ab-picker-item ${
                            isSelected ? 'ab-picker-item--selected' : ''
                        }`}
                        key={`${item.countryName}-${i}`}
                        onClick={() => handleChange(item?.countryCode)}
                        aria-label={item?.countryName}
                        aria-selected={isSelected}
                    >
                        <div className="ab-picker-item--country-flag">
                            <img
                                src={item?.countryImg}
                                alt={`${item?.countryName} flag`}
                            />
                        </div>

                        <span className="ab-picker-hypen">-</span>
                        <span className="ab-picker-item--country-code">
                            {item?.countryName}
                        </span>
                    </div>
                );
            }

            if (type === 'currency') {
                isSelected = pickedValue?.currencyName === item?.currencyName;
                itemJSX = (
                    <div
                        className={`ab-picker-item-currerncy ${
                            isSelected ? 'ab-picker-item--selected' : ''
                        }`}
                        aria-label={item?.currencyName}
                        aria-selected={isSelected}
                        key={`${item.currencyID}-${i}`}
                        onClick={() => handleChange(item?.currencyCode)}
                    >
                        <span className="ab-picker-item--currency-symbol">
                            {item?.currencySymbol}
                        </span>
                        <span className="ab-picker-hypen">-</span>
                        <span className="ab-picker-item--currency-code">
                            {item?.currencyCode}
                        </span>
                        {/* <span>{item?.currencyName}</span> */}
                    </div>
                );
            }

            col.push(itemJSX);

            if (
                (i + 1) % actualNumberOfItemsInCol === 0 ||
                i === numberOfItems - 1
            ) {
                cols.push(col);
                col = [];
            }
        }

        return cols.map((col, index) => {
            return <div key={`picker-col-${type}-${index}`}>{col}</div>;
        });
    };

    // TODO: MAYBE CHANGE THIS
    const renderedItems = useMemo(() => renderItems(), [data, onChange]);

    const handleChange = (newPickedValue) => {
        let newPickedItem;
        if (type === 'country') {
            newPickedItem = data.find((item) => {
                return item.countryCode === newPickedValue;
            });
            // TODO: PROBABLY NEED SOMETHING - NEEDS TO COME FROM THE BACKEND
            onChange(newPickedItem.countryCode);
        }

        if (type === 'currency') {
            newPickedItem = data.find((item) => {
                return item.currencyCode === newPickedValue;
            });

            onChange(newPickedItem.currencyCode);
        }

        setPickedValue(newPickedItem);
        setIsOpen(false);
    };

    const handleClickOutside = () => {
        setIsOpen(false);
    };

    useOnClickOutside(pickerRef, handleClickOutside);

    return (
        data?.length > 0 && (
            <div className="ab-picker" ref={pickerRef}>
                <button
                    className="ab-picker-trigger-btn"
                    onClick={handleTiggerBtnClick}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                >
                    {type === 'currency' && (
                        <span>{pickedValue?.currencyCode}</span>
                    )}
                    {type === 'country' && (
                        <img
                            src={pickedValue?.countryImg}
                            alt={`${pickedValue?.countryName} flag`}
                        />
                    )}{' '}
                    <MdKeyboardArrowDown size={18} />
                </button>

                {isOpen && (
                    <div
                        className="ab-picker-content"
                        role="dialog"
                        aria-label={ariaLabel}
                    >
                        {title && (
                            <div className="ab-picker-header">
                                <span className="ab-picker-title">{title}</span>
                                <Button
                                    className="ab-btn-info--room-details"
                                    onClick={handleTiggerBtnClick}
                                    size={14}
                                >
                                    <FaTimes />
                                </Button>
                            </div>
                        )}
                        <div className="ab-picker-body">{renderedItems}</div>
                    </div>
                )}
            </div>
        )
    );
};

Picker.propTypes = {
    data: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                countryName: PropTypes.string,
                countryCode: PropTypes.string,
                countryImg: PropTypes.string,
            })
        ),
        PropTypes.arrayOf(
            PropTypes.shape({
                currencySymbol: PropTypes.string,
                currencyName: PropTypes.string,
                currencyID: PropTypes.string,
                currencyCode: PropTypes.string,
            })
        ),
    ]),
    onChange: PropTypes.func,
    type: PropTypes.oneOf(['country', 'currency']),
    value: PropTypes.oneOfType([
        PropTypes.shape({
            currencySymbol: PropTypes.string,
            currencyName: PropTypes.string,
            currencyID: PropTypes.string,
            currencyCode: PropTypes.string,
        }),
        PropTypes.shape({
            countryName: PropTypes.string,
            countryCode: PropTypes.string,
            countryImg: PropTypes.string,
        }),
    ]),
    title: PropTypes.string,
};

export default Picker;
